import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { PageHeader } from '@ipfabric/ui-components/PageHeader/PageHeader';
import { filesize } from 'filesize';
import { useSupportStatusQuery } from 'pages/Support/Status/useSupportStatusQuery';
import { memo } from 'react';
import { Helmet } from 'react-helmet';
import { renderQuery } from 'utils/react-query/renderQuery';
import { HardwareLoadCard } from './HardwareLoadCard';
import { McpServerServiceStatusRow } from './McpServerServiceStatusRow';
import { ServiceRangeChart } from './ServiceRangeChart';
import { ServiceStatusRow } from './ServiceStatusRow';

export const SupportStatusPage = memo(() => {
  const statusQuery = useSupportStatusQuery();
  const refreshStatus = () => {
    statusQuery.refetch();
  };

  return (
    <div className='flex flex-col items-center'>
      <Helmet title='System status' />
      <PageHeader
        elevated
        width='content-default'
        label='Status page'
        buttons={
          <Button
            variant='outline'
            onClick={refreshStatus}
            loading={statusQuery.fetchStatus === 'fetching'}
          >
            Refresh status
          </Button>
        }
      />
      <div className='w-full max-w-content-default px mt-2xl mb'>
        {renderQuery({
          query: statusQuery,
          renderStaleDataOnMount: false,
          success: (data) => (
            <>
              <div className='flex flex-row gap items-stretch mb'>
                <Card className='flex-1 pb'>
                  <div className='p text-level-5 font-semibold'>System</div>
                  <ServiceStatusRow
                    status={data.postgresql}
                    label='PostgreSQL DB'
                    serviceName='postgresql@17-main'
                    onServiceRestarted={refreshStatus}
                  />
                  <ServiceStatusRow
                    status={data.rabbit}
                    label='Rabbit MQ'
                    serviceName='rabbitmq-server'
                    onServiceRestarted={refreshStatus}
                  />
                  <ServiceStatusRow
                    status={data.syslogWorker}
                    label='Configuration Management Worker'
                    serviceName='ipf-discovery-syslogworker'
                    onServiceRestarted={refreshStatus}
                  />
                  <ServiceStatusRow
                    status={data.webhookWorker}
                    label='Webhook Worker'
                    serviceName='ipf-webhook-worker'
                    onServiceRestarted={refreshStatus}
                  />
                  <ServiceStatusRow
                    status={data.kafka}
                    label='Kafka'
                    serviceName='ipf-kafka'
                    onServiceRestarted={refreshStatus}
                  />
                  <McpServerServiceStatusRow
                    status={data.mcpServer}
                    label='MCP Server'
                    serviceName='ipf-mcp-server'
                    onServiceRestarted={refreshStatus}
                  />
                </Card>
                <Card className='flex-1 pb'>
                  <div className='p text-level-5 font-semibold'>Discovery</div>
                  <ServiceStatusRow
                    // reverse-engineered from old System Administration code - we consider
                    // discovery to be running when tasker is running; in that case we offer
                    // restart of discovery services.
                    canRestart={data.tasker}
                    discoveryRunning={data.tasker}
                    status={data.tasker}
                    label='Tasker'
                    serviceName='ipf-discovery-tasker'
                    onServiceRestarted={refreshStatus}
                  />
                  <ServiceStatusRow
                    canRestart={data.tasker}
                    discoveryRunning={data.tasker}
                    status={data.worker}
                    label='Workers'
                    serviceName='ipf-discovery-worker'
                    onServiceRestarted={refreshStatus}
                  />
                  <ServiceStatusRow
                    canRestart={data.tasker}
                    discoveryRunning={data.tasker}
                    status={data.netWorker}
                    label='Net Worker'
                    serviceName='ipf-discovery-networker'
                    onServiceRestarted={refreshStatus}
                  />
                  <ServiceStatusRow
                    canRestart={data.tasker}
                    discoveryRunning={data.tasker}
                    status={data.scanWorker}
                    label='Scanner Workers'
                    serviceName='ipf-discovery-scanworker'
                    onServiceRestarted={refreshStatus}
                  />
                </Card>
              </div>
              <div className='flex flex-row gap items-stretch mb'>
                <HardwareLoadCard
                  title='System load'
                  glyph='fa-microchip'
                  overall={data.parsed.cpu.load1m}
                  thresholdPercent={data.parsed.cpu.load1mPerCorePercent}
                  details={[
                    ['Last 5 min', data.parsed.cpu.load5m?.toFixed(2) ?? '-'],
                    ['Last 15 min', data.parsed.cpu.load15m?.toFixed(2) ?? '-'],
                    ['CPU cores', data.parsed.cpu.count?.toString() ?? '-'],
                  ]}
                />
                <HardwareLoadCard
                  title='Memory usage'
                  glyph='fa-memory'
                  overall={data.parsed.memory.usedPercent ?? null}
                  isPercent
                  details={[
                    ['Free', data.parsed.memory.freeSize ?? '-'],
                    ['Total', data.parsed.memory.totalSize ?? '-'],
                  ]}
                />
                <HardwareLoadCard
                  title='Disk usage'
                  glyph='fa-hard-drive'
                  overall={data.disk.usedPct}
                  isPercent
                  details={[
                    ['Free', filesize(data.disk.free)],
                    ['Total', filesize(data.disk.total)],
                  ]}
                />
              </div>
              <div className='flex flex-row gap items-stretch'>
                <ServiceRangeChart
                  title='CPU usage over time'
                  series={data.cpuRange}
                  formatY={(v) => `${v.toFixed(1)} %`}
                />
                <ServiceRangeChart
                  title='Memory usage over time'
                  series={data.memoryRange}
                  formatY={(v) => filesize(v)}
                />
              </div>
            </>
          ),
        })}
      </div>
    </div>
  );
});
